import { StartFreeButton } from '@/components/shared/StartFreeButton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useHasActivePlan } from '@/hooks/useHasActivePlan';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { ArrowRight, BookUp, Coins, Loader2, LogOut, Rocket, Settings, ShoppingBag, Sparkles, UserCircle2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';


interface UserMenuProps {
  user: any;
}

export function UserMenu({ user }: UserMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { data: isAdmin } = useIsAdmin();
  const [isReferralLoading, setIsReferralLoading] = useState(false);

  const PRODUCT_ID = 'bookstera';


  const { data: hasBooks } = useQuery({
    queryKey: ['user-has-books', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data, error } = await supabase
        .from('books')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (error) throw error;
      return data && data.length > 0;
    },
    enabled: !!user?.id,
  });

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/signin');
      toast({
        title: 'Signed out successfully',
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error signing out',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };



  const fetchReferralAuthToken = useCallback(async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase.functions.invoke(
      'generate-referral-token',
      {
        body: { userId: user.id },
      }
    );

    if (error) {
      throw error;
    }

    const token = data?.token;
    if (!token) {
      throw new Error('Missing referral auth token');
    }

    return token as string;
  }, [user?.id]);

  const handleShowReferralWidget = useCallback(async () => {
    if (!user?.id) {
      navigate('/signin');
      return;
    }

    if (!PRODUCT_ID) {
      console.warn('Reditus product ID is not configured.');
      return;
    }

    const reditus = (window as { gr?: (...args: any[]) => void }).gr;
    if (!reditus) {
      console.warn('Reditus widget is not available on window.gr yet.');
      return;
    }

    try {
      setIsReferralLoading(true);
      const authToken = await fetchReferralAuthToken();

      const metadata = user.user_metadata ?? {};
      const firstName =
        metadata.first_name ||
        metadata.given_name ||
        metadata.full_name?.split(' ')?.[0] ||
        '';
      const lastName =
        metadata.last_name ||
        metadata.family_name ||
        metadata.full_name?.split(' ')?.slice(1).join(' ') ||
        '';

      reditus('loadReferralWidget', {
        product_id: PRODUCT_ID,
        auth_token: authToken,
        user_details: {
          user_id: user.id,
          email: user.email,
          first_name: firstName,
          last_name: lastName,
          company_id: metadata.company_id || metadata.companyId || undefined,
          company_name: metadata.company_name || metadata.companyName || undefined,
        },
      });

      const waitAndShowWidget = async (attempt = 1): Promise<boolean> => {
        const referralWidget = (window as typeof window & {
          referralWidget?: { show?: () => void };
        }).referralWidget;

        if (referralWidget?.show) {
          referralWidget.show();
          return true;
        }

        if (attempt >= 15) {
          console.warn(
            'Reditus referral widget show method not available after retries.'
          );
          return false;
        }

        await new Promise((resolve) => setTimeout(resolve, 250));
        return waitAndShowWidget(attempt + 1);
      };

      await waitAndShowWidget();
    } catch (error) {
      console.error('Failed to open referral widget:', error);
    } finally {
      setIsReferralLoading(false);
    }
  }, [fetchReferralAuthToken, navigate, user]);

  function UpgradeOrPricingMenuItem() {
    const { hasPaidFeatures } = useHasActivePlan();
    if (hasPaidFeatures) return null;
    return (
      <DropdownMenuItem
        onClick={() => navigate('/pricing')}
        className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-amber-700 transition hover:bg-amber-50 hover:text-amber-800"
      >
        <Rocket className="h-4 w-4 text-amber-500" />
        Upgrade Plan
      </DropdownMenuItem>
    );
  }

  if (!user) {
    return (
      <div className="flex w-full items-center justify-end gap-1.5">
        <Button
          variant="ghost"
          className="hidden rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-800 sm:inline-flex"
          onClick={() =>
            navigate('/signin', { state: { from: location.pathname } })
          }
        >
          Sign In
        </Button>
        <StartFreeButton useHomepageAuthDestination />
      </div>
    );
  }


  return (
    <>
      {user && (
        <div className="ml-auto">
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleShowReferralWidget}
                  disabled={isReferralLoading}
                  aria-label="Refer & Earn Credits"
                  className="h-9 w-9 rounded-full text-amber-500 transition-all duration-200 hover:bg-amber-50 hover:text-amber-600"
                >
                  {isReferralLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Coins className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isReferralLoading ? 'Opening...' : 'Refer & earn credits'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      <Button
        onClick={() => navigate('/create-book')}
        size="sm"
        className="inline-flex rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold shadow-sm transition-all duration-200 hover:shadow-md"
      >
        <span className="hidden sm:inline">Create Book</span>
        <span className="sm:hidden">Create</span>
      </Button>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full border border-white/40 bg-white/80 p-2 text-slate-700 shadow-sm backdrop-blur transition hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
          >
            <UserCircle2 className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56 overflow-hidden rounded-2xl border border-slate-100 bg-white/90 p-0 shadow-xl backdrop-blur"
        >
          <div className="bg-gradient-to-r from-primary/15 via-secondary/10 to-primary-dark/15 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.3em] text-slate-500">
              Signed in as
            </div>
            <div className="mt-1 text-xs font-semibold text-slate-900">
              {user.email ?? 'Bookstera Creator'}
            </div>
          </div>
          <div className="grid gap-1 p-2">
            <DropdownMenuItem
              onClick={() => { posthog.capture('user_menu_book_growth_clicked'); navigate('/book-growth'); }}
              className="group flex items-center gap-2 rounded-lg bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 transition hover:bg-orange-100"
            >
              <BookUp className="h-4 w-4 text-orange-600" />
              Book Growth Hub
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/profile')}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
            >
              <Settings className="h-4 w-4 text-primary/60" />
              Profile & Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/create-book')}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
            >
              <Rocket className="h-4 w-4 text-primary/60" />
              Create Book
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/my-books')}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
            >
              <Sparkles className="h-4 w-4 text-primary/60" />
              My Books
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigate('/purchases')}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
            >
              <ShoppingBag className="h-4 w-4 text-primary/60" />
              E-book Purchases
            </DropdownMenuItem>
            <UpgradeOrPricingMenuItem />
            {hasBooks && (
              <DropdownMenuItem
                onClick={() => navigate('/author-dashboard')}
                className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
              >
                <ArrowRight className="h-4 w-4 text-primary/60" />
                Author Dashboard
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem
                onClick={() => navigate('/admin-dashboard')}
                className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
              >
                <ArrowRight className="h-4 w-4 text-primary/60" />
                Admin Dashboard
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => navigate('/pseudonyms')}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-primary/5 hover:text-primary"
            >
              <ArrowRight className="h-4 w-4 text-primary/60" />
              Pseudonyms
            </DropdownMenuItem>
          </div>
          <div className="border-t border-slate-100 bg-white/90 p-2">
            <DropdownMenuItem
              onClick={handleSignOut}
              className="group flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-red-500 transition hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
